import IdvIntro from '@/components/idv/IdvIntro';
import { useTrackPageViewed } from '@/services/analytics/useTracking';
import { useUserData } from '@/services/user';
import {
    EMPLOYMENT_INDUSTRY_OPTIONS,
    EMPLOYMENT_ROLE_OPTIONS,
    EMPLOYMENT_STATUS_OPTIONS,
} from '@common/inputs';
import {
    EmploymentIndustry,
    EmploymentRole,
    EmploymentStatus,
} from '@common/types';
import React, { useState } from 'react';
import { IdvForm, IdvFormType } from '../IdvForm';

type T = IdvFormType<
    {
        employmentStatus?: EmploymentStatus;
        employerName?: string;
        industry?: EmploymentIndustry;
        role?: EmploymentRole;
        employmentStartYearMonth?: string;
    },
    'annualIncome'
>;

export const EmploymentStatusView = () => {
    useTrackPageViewed('IDV > EmploymentStatusView');

    const { userInfo } = useUserData();
    const [status, setStatus] = useState<T['employmentStatus']>(
        userInfo?.onboardingData?.employmentStatus || undefined
    );
    return (
        <>
            <IdvIntro
                title="Are you currently employed?"
                desc="We use this information to verify your identity."
            />
            <IdvForm<T>
                initValues={(userProfile) => ({
                    employmentStatus: status,
                    employerName: userProfile?.onboardingData?.employerName,
                    employmentStartYearMonth:
                        userProfile?.onboardingData?.employmentStartYearMonth,
                    industry: userProfile?.onboardingData?.industry,
                    role: userProfile?.onboardingData?.role,
                    annualIncome: userProfile?.annualIncome,
                })}
                toMutationInput={({ annualIncome, ...rest }) => ({
                    annualIncome,
                    onboardingData: rest,
                })}
                backNextNavProps={{
                    onClickEventParams: {
                        location: 'IDV > EmploymentStatusView',
                    },
                }}
            >
                <IdvForm.Row>
                    <IdvForm.Input<T>
                        name="employmentStatus"
                        label={`Employment status`}
                        placeholder="Employed"
                        type="select"
                        options={EMPLOYMENT_STATUS_OPTIONS}
                        onChange={(value, updateValues) => {
                            setStatus(value);
                            // Clear fields unrelated to selected status
                            if (value === 'EMPLOYED') {
                                updateValues?.({
                                    employmentStartYearMonth: undefined,
                                });
                            } else if (value === 'SELF_EMPLOYED') {
                                updateValues?.({
                                    employerName: undefined,
                                });
                            } else {
                                updateValues?.({
                                    employerName: undefined,
                                    employmentStartYearMonth: undefined,
                                    industry: undefined,
                                    role: undefined,
                                });
                            }
                        }}
                        isRequired
                        autoFocus
                    />
                    <IdvForm.Input<T>
                        name="annualIncome"
                        label="Annual income"
                        placeholder="Enter income"
                        type="currency"
                        inputOptions={{ preserveEmpty: true }}
                        isRequired
                    />
                </IdvForm.Row>
                {status === 'SELF_EMPLOYED' && (
                    <>
                        <IdvForm.Row>
                            <IdvForm.Input<T>
                                name="employmentStartYearMonth"
                                label="Start date for self employment"
                                type="yearMonth"
                                yearMonthOptions={{
                                    allowPastDateSelection: true,
                                    minYear: 1970,
                                    maxYear: new Date().getFullYear(),
                                    yearOrder: 'DESC',
                                }}
                                isRequired
                            />
                            <IdvForm.Input<T>
                                name="industry"
                                label="Your industry"
                                placeholder="Select an industry"
                                type="select"
                                options={EMPLOYMENT_INDUSTRY_OPTIONS}
                                isRequired
                            />
                        </IdvForm.Row>
                        <IdvForm.Row>
                            <IdvForm.Input<T>
                                name="role"
                                label="Your role"
                                placeholder="Select a role"
                                type="select"
                                options={EMPLOYMENT_ROLE_OPTIONS}
                                isRequired
                            />
                        </IdvForm.Row>
                    </>
                )}
                {status === 'EMPLOYED' && (
                    <>
                        <IdvForm.Row>
                            <IdvForm.Input<T>
                                name="employerName"
                                label="Employer name"
                                placeholder="ACME Corporation"
                                isRequired
                            />
                            <IdvForm.Input<T>
                                name="industry"
                                label="Employer industry"
                                placeholder="Select an industry"
                                type="select"
                                options={EMPLOYMENT_INDUSTRY_OPTIONS}
                                isRequired
                            />
                        </IdvForm.Row>
                        <IdvForm.Row>
                            <IdvForm.Input<T>
                                name="role"
                                label="Your role at employer"
                                placeholder="Select a role"
                                type="select"
                                options={EMPLOYMENT_ROLE_OPTIONS}
                                isRequired
                            />
                        </IdvForm.Row>
                    </>
                )}
            </IdvForm>
        </>
    );
};
